import { useEffect, useRef } from "react";

export interface AIGenerationDraftSyncPropsI {
  /** Latest AI-generated subject, or undefined when nothing was generated. */
  subject?: string;
  /** Latest AI-generated body, or undefined when nothing was generated. */
  body?: string;
  /** Whether a generation is streaming right now. */
  isGenerating?: boolean;
  onApplySubject: (subject: string) => void;
  onApplyBody: (body: string) => void;
}

/**
 * Feeds an AI generation into the email draft, exactly once per generation.
 *
 * The generation result lives in EmailTemplatesProvider and is never cleared,
 * while the compose form under it unmounts every time the SDR switches to the
 * History tab. Effects always run on mount, so applying the generation from a
 * bare `useEffect` meant that coming back to Compose re-applied a generation
 * the SDR had already edited over - and the raw AI draft went to the prospect
 * instead of the edited one.
 *
 * Seeding the "already applied" refs from the values present at mount makes
 * applied the default for anything that predates this mount. Only a value that
 * changes while this instance is alive - a stream chunk, or a regeneration -
 * reaches the draft.
 *
 * Unless a generation is streaming at mount. The stream is owned by the
 * provider and keeps running while the compose form is unmounted, so if the SDR
 * leaves the tab mid-generation the draft is left holding a truncated body.
 * Seeding in that state would mark the finished text as applied and strand the
 * draft on the truncation, so nothing is seeded while `isGenerating`.
 */
export const useAIGenerationDraftSync = ({
  subject,
  body,
  isGenerating,
  onApplySubject,
  onApplyBody,
}: AIGenerationDraftSyncPropsI) => {
  const appliedSubjectRef = useRef(isGenerating ? undefined : subject);
  const appliedBodyRef = useRef(isGenerating ? undefined : body);

  /**
   * The callbacks close over the current draft. Holding the newest pair keeps
   * a chunk arriving mid-stream from writing through a draft snapshot taken
   * when the stream started.
   */
  const onApplySubjectRef = useRef(onApplySubject);
  const onApplyBodyRef = useRef(onApplyBody);

  // Declared before the two effects below so they always see the newest pair.
  useEffect(() => {
    onApplySubjectRef.current = onApplySubject;
    onApplyBodyRef.current = onApplyBody;
  });

  useEffect(() => {
    /**
     * An empty value means a generation is starting - Compose with AI blanks
     * subject and body before it streams. Nothing from the previous generation
     * counts as applied from here on, so a regeneration that happens to match
     * the last one still reaches the draft.
     */
    if (!subject) {
      appliedSubjectRef.current = undefined;
      return;
    }

    if (subject === appliedSubjectRef.current) {
      return;
    }

    appliedSubjectRef.current = subject;
    onApplySubjectRef.current(subject);
  }, [subject]);

  useEffect(() => {
    if (!body) {
      appliedBodyRef.current = undefined;
      return;
    }

    if (body === appliedBodyRef.current) {
      return;
    }

    appliedBodyRef.current = body;
    onApplyBodyRef.current(body);
  }, [body]);
};
